import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Instagram } from 'lucide-react';
import { trackMeta } from '../lib/metaPixel';

const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

const Footer = () => {
  const handleEmailClick = () => {
    trackEvent('email_click', {
      email_address: 'info@yoursetl.co.uk',
      click_location: 'footer'
    });
    trackMeta('Contact', { content_name: 'Email Click - Footer' });
  };

  const handleInstagramClick = () => {
    trackEvent('instagram_click', {
      instagram_handle: '@yoursetl',
      click_location: 'footer'
    });
  };

  return (
    <footer className="bg-[#2a231d] text-[#f5f1e8] py-12">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-8">
          {/* Brand */}
          <div>
            <h3
              className="text-3xl font-light tracking-wider mb-4 text-[#d4a574]"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Your Setl
            </h3>
            <p
              className="text-sm leading-relaxed text-[#f5f1e8]/80 mb-4"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Your place in nature. Slow down, reconnect and find balance again on the Dorset coast.
            </p>
            <div className="flex items-center space-x-3">
              <Instagram size={20} className="text-[#d4a574]" />
              <a
                href="https://www.instagram.com/yoursetl"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleInstagramClick}
                className="text-sm text-[#f5f1e8]/80 hover:text-[#d4a574] transition-colors duration-300"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                @yoursetl
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              className="text-lg font-light mb-4 tracking-wide"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Explore
            </h4>
            <ul className="space-y-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              {[
                { name: 'Home', path: '/' },
                { name: 'Your Country Cabin', path: '/your-country-cabin' },
                { name: 'Your Host', path: '/your-host' },
                { name: 'Your Landscape', path: '/your-landscape' },
                { name: 'Brochure', path: '/brochure' },
                { name: 'Pitch Fee', path: '/pitch-fee' },
                { name: 'Contact', path: '/contact' },
              ].map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className="text-sm text-[#f5f1e8]/80 hover:text-[#d4a574] transition-colors duration-300"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
              <li>
                <span className="text-sm text-[#f5f1e8]/40 cursor-default">
                  Press <span className="text-xs">(coming soon)</span>
                </span>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <Link
              to="/contact"
              className="text-lg font-light mb-4 tracking-wide block hover:text-[#d4a574] transition-colors duration-300"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Connect
            </Link>
            <ul className="space-y-3" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li className="flex items-start space-x-3">
                <Mail size={18} className="text-[#d4a574] mt-1 flex-shrink-0" />
                <a
                  href="mailto:info@yoursetl.co.uk"
                  onClick={handleEmailClick}
                  className="text-sm text-[#f5f1e8]/80 hover:text-[#d4a574] transition-colors duration-300"
                >
                  info@yoursetl.co.uk
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-[#d4a574] mt-1 flex-shrink-0" />
                <a
                  href="https://www.google.com/maps/search/Jurassic+Coast+Dorset+United+Kingdom"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#f5f1e8]/80 hover:text-[#d4a574] transition-colors duration-300"
                >
                  Dorset, Jurassic Coast<br />United Kingdom
                </a>
              </li>
              <li className="mt-3">
                <div className="rounded overflow-hidden">
                  <iframe
                    title="Your Setl location"
                    src="https://maps.google.com/maps?q=50.65727270734,-2.5271115865080964&t=k&z=15&ie=UTF8&iwloc=&output=embed"
                    width="100%"
                    height="150"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#f5f1e8]/20 pt-8">
          <p
            className="text-center text-sm text-[#f5f1e8]/60"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            &copy; {new Date().getFullYear()} Your Setl. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
